import { Row, Table } from '@tanstack/react-table';
import { useCallback, useState } from 'react';

import { GROUP_ROW_MARKER } from '../components/row-render/components/grouped/GroupedV8';
import { SELECT_BOX_MARKER } from '../utils/makeRowsSelectable';

interface UseRowClickProps<T extends object> {
  table: Table<T>;
  tableBodyRef: React.RefObject<HTMLDivElement>;
}

export function useSelectionRowClickV8<
  T extends object = object,
  TRow extends Row<T> = Row<T>
>(props: UseRowClickProps<T>) {
  const { tableBodyRef, table } = props;

  const [lastInteraction, setLastInteraction] = useState<{
    rowId: string;
    selected: boolean;
  } | null>(null);

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>, row: TRow) => {
      // Check if the click event happened inside the table or if it's coming from a portal.
      if (
        !(e.target instanceof Node && tableBodyRef.current?.contains(e.target))
      ) {
        // Event originated in a portal. Ignore.
        return;
      }
      const ctrlKey = e.ctrlKey || e.metaKey;
      const selectableMultiple = row.getCanMultiSelect();
      const element = e.target as Element;
      // If clicked on a group row (except the checkbox)
      if (element.closest(`[data-${GROUP_ROW_MARKER}=true]`)) {
        if (!element.closest(`[data-${SELECT_BOX_MARKER}=true]`)) return;
      }

      // When in single-selection mode, select only the clicked row.
      if (!selectableMultiple) {
        table.setRowSelection({ [row.id]: true });
        setLastInteraction({ rowId: row.id, selected: true });
        return;
      }

      const shiftKey = e.shiftKey;
      const clickedCheckbox = element.closest(
        `[data-${SELECT_BOX_MARKER}=true]`
      );

      if (shiftKey) {
        if (ctrlKey && !lastInteraction) {
          return;
        }

        const selection = ctrlKey ? { ...table.getState().rowSelection } : {};

        const start =
          lastInteraction !== null
            ? table
                .getRowModel()
                .flatRows.findIndex((r) => r.id === lastInteraction.rowId)
            : 0;
        const end = table
          .getRowModel()
          .flatRows.findIndex((r) => r.id === row.id);

        if (start === -1 || end === -1) return;

        const setSelected = !ctrlKey
          ? true
          : lastInteraction?.selected ?? false;

        for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
          const row = table.getRowModel().flatRows[i];
          if (!row.getCanSelect()) break;
          selection[row.id] = setSelected;
        }

        table.setRowSelection(selection);
      } else {
        let setSelected = !row.getIsSelected();

        let selection = { ...table.getState().rowSelection };

        if (!ctrlKey && !clickedCheckbox && !table.options.isUnderPressSelect) {
          setSelected = true;
          selection = { [row.id]: true };
        }

        selection[row.id] = setSelected;
        table.setRowSelection(selection);
        setLastInteraction({ rowId: row.id, selected: setSelected });
      }
    },
    [tableBodyRef, table, lastInteraction]
  );

  return onRowClick;
}
